import { Ref } from 'vue'
import dataStorageManager, { BucketDefaultIDs, DMS_Operation } from '../../managers/data/DataStorageManager'
import dataQueryManager from '../../managers/network/DataQueryManager'
import { emailify, rutify, cleanNil, cleanNilNumber } from '../apiUtils'
import { IRequest } from './RequestsServices'
import { RequestedChange } from './ChangesServices'
import { ICobro, OperatedCobro } from './CobrosServices'
import { IService, OperatedService } from './ServicesServices'
import { IViaje, OperatedViaje } from './ViajesServices'
import { UnidadMedidaAplicada } from './TarifasServices'

export type TipoEmbarcacion = 'pilotaje' | 'auxiliar' | 'turistica' | 'fletera'

export interface IEmbarcacion {
  id: number
  nombre_embarcacion: string
  numero_matricula: number | string
  puerto: string
  eslora_metros: number
  manga_metros: number
  tipo_embarcacion: TipoEmbarcacion
  capacidad_pasajeros: number
  capacidad_pasajeros_adicional: number
  nave_madre: string
  id_lancha: number
  tripulacion_exigida: number
  fecha_creacion: string
  fecha_actualizacion: string
  usuario_creacion: number
  activo: boolean
  id_matricula: number
  id_fotografía: number
  id_certifricado_navegacion: number
  id_copropiedad?: number
  anio_fabricacion: string
  fecha_vencimiento: string | null
  convenio: number
}

export interface OperatedEmbarcacion extends IEmbarcacion {
  requestData: IRequest | undefined
  aoRequestData?: IRequest
  aoData?: any
  changesData: RequestedChange[]
  asociationData?: IRequest
  asociationsData: IRequest[]
  cobrosData: OperatedCobro[]
  currentCobrosData: OperatedCobro[]
  salidasData: OperatedViaje[]
  serviciosData: OperatedService[]
  unidadesData?: UnidadMedidaAplicada
}

export const defaultIEmbarcacion: IEmbarcacion = {
  id: 0,
  nombre_embarcacion: '',
  numero_matricula: 0,
  puerto: '',
  eslora_metros: 0.01,
  manga_metros: 0.01,
  tipo_embarcacion: 'turistica',
  capacidad_pasajeros: 0,
  capacidad_pasajeros_adicional: 0,
  nave_madre: '',
  id_lancha: 0,
  tripulacion_exigida: 0,
  fecha_creacion: '',
  fecha_actualizacion: '',
  usuario_creacion: 0,
  activo: false,
  id_matricula: 0,
  id_fotografía: 0,
  id_certifricado_navegacion: 0,
  id_copropiedad: 0,
  anio_fabricacion: '2025-01-01T12:00:00.000+00:00',
  fecha_vencimiento: '1970-01-01T00:00:00.000Z',
  convenio: 0,
}

export default class EmbarcacionesServices {
  private bucketID: string | null = null

  constructor() {
    // Registrar el bucket para embarcaciones
    this.bucketID = dataStorageManager.registerBucket<IEmbarcacion>(
      this.getAllEmbarcaciones.bind(this), // Asegúrate de enlazar el contexto correcto
      {
        refreshRatio: 5 * 60 * 1000, // Refrescar cada 5 minutos
        //preload: true, // Preload
        bucketID: BucketDefaultIDs.EMBARCACIONES, // ID del bucket
        operations: new Map<string, DMS_Operation>([
          [
            'insertEmbarcacion',
            {
              operation: this.insertEmbarcacion.bind(this),
              reloadAfter: true,
            },
          ],
          [
            'updateEmbarcacion',
            {
              operation: this.updateEmbarcacion.bind(this),
              reloadAfter: true,
            },
          ],
        ]), // Las operaciones se añadiran despues
      },
    )
  }

  startup() {}

  private sanitizeEmbarcacion(embarcacion: any): IEmbarcacion {
    return {
      ...embarcacion,
      id: cleanNilNumber(embarcacion.id, 0),
      nombre_embarcacion: cleanNil(embarcacion.nombre_embarcacion, ''),
      numero_matricula: cleanNil(embarcacion.numero_matricula, ''),
      puerto: cleanNil(embarcacion.puerto, ''),
      eslora_metros: cleanNilNumber(embarcacion.eslora_metros, 0),
      manga_metros: cleanNilNumber(embarcacion.manga_metros, 0),
      tipo_embarcacion: cleanNil(embarcacion.tipo_embarcacion, 'turistica'),
      capacidad_pasajeros: cleanNilNumber(embarcacion.capacidad_pasajeros, 0),
      capacidad_pasajeros_adicional: cleanNilNumber(embarcacion.capacidad_pasajeros_adicional, 0),
      nave_madre: cleanNil(embarcacion.nave_madre, ''),
      id_lancha: cleanNilNumber(embarcacion.id_lancha, 0),
      tripulacion_exigida: cleanNilNumber(embarcacion.tripulacion_exigida, 0),
      usuario_creacion: cleanNilNumber(embarcacion.usuario_creacion, 0),
      id_matricula: cleanNilNumber(embarcacion.id_matricula, 0),
      id_fotografía: cleanNilNumber(embarcacion.id_fotografía, 0),
      id_certifricado_navegacion: cleanNilNumber(embarcacion.id_certifricado_navegacion, 0),
      id_copropiedad: cleanNilNumber(embarcacion.id_copropiedad, 0),
      anio_fabricacion: cleanNil(embarcacion.anio_fabricacion, ''),
      fecha_vencimiento: (() => {
        let fv = cleanNil<string | null>(embarcacion.fecha_vencimiento, null)
        if (typeof fv === 'string' && fv.includes('+') && !fv.includes('T')) {
          fv = fv.split('+')[0]
        }
        return fv
      })(),
      convenio: cleanNilNumber(embarcacion.convenio, 0),
    }
  }

  async getAllEmbarcaciones(): Promise<IEmbarcacion[]> {
    try {
      const response = await dataQueryManager.addQuery<IEmbarcacion | IEmbarcacion[]>({
        query: `${import.meta.env.VITE_BASE_URL}/enrolamiento/1/op_select_embarcaciones`,
        operation: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        params: [],
      })
      if (response.data && Array.isArray(response.data)) {
        return response.data.map((embarcacion) => this.sanitizeEmbarcacion(embarcacion))
      } else if (response.data) {
        return [response.data].map((embarcacion) => this.sanitizeEmbarcacion(embarcacion))
      }
    } catch (error) {
      console.error('Error fetching users:', error)
    }
    return []
  }

  private formatYearToISO(val: any): string {
    if (!val || typeof val === 'object') return '1970-01-01T00:00:00.000Z'
    const s = String(val).trim()
    if (s.length <= 4 && !isNaN(parseInt(s))) {
      return new Date(parseInt(s), 0, 1).toISOString()
    }
    if (s.length < 19) {
      const d = new Date(s)
      return !isNaN(d.getTime()) ? d.toISOString() : '1970-01-01T00:00:00.000Z'
    }
    return s
  }

  async insertEmbarcacion(
    embarcacionData: Omit<IEmbarcacion, 'id' | 'fecha_creacion' | 'fecha_actualizacion' | 'activo'>,
  ): Promise<number | false> {
    try {
      const body = {
        op_insert_embarcaciones: {
          nombre_embarcacion: embarcacionData.nombre_embarcacion,
          numero_matricula: embarcacionData.numero_matricula,
          puerto: embarcacionData.puerto,
          eslora_metros: embarcacionData.eslora_metros,
          manga_metros: embarcacionData.manga_metros,
          tipo_embarcacion: embarcacionData.tipo_embarcacion,
          capacidad_pasajeros: embarcacionData.capacidad_pasajeros,
          capacidad_pasajeros_adicional: embarcacionData.capacidad_pasajeros_adicional,
          nave_madre: embarcacionData.nave_madre,
          id_lancha: embarcacionData.id_lancha,
          tripulacion_exigida: embarcacionData.tripulacion_exigida,
          usuario_creacion: embarcacionData.usuario_creacion,
          id_matricula: embarcacionData.id_matricula,
          id_fotografía: embarcacionData.id_fotografía,
          id_certifricado_navegacion: embarcacionData.id_certifricado_navegacion,
          anio_fabricacion: this.formatYearToISO(embarcacionData.anio_fabricacion),
          fecha_vencimiento:
            !embarcacionData.fecha_vencimiento ||
            typeof embarcacionData.fecha_vencimiento === 'object' ||
            embarcacionData.fecha_vencimiento.includes('@nil')
              ? '1970-01-01T00:00:00.000Z'
              : embarcacionData.fecha_vencimiento,
          convenio: embarcacionData.convenio,
        },
      }

      const response = await dataQueryManager.addQuery<number>({
        query: `${import.meta.env.VITE_BASE_URL}/enrolamiento/1/op_insert_embarcaciones`,
        operation: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        params: [],
        body: body,
      })

      // El backend (op_insert_embarcaciones) devuelve el id generado vía RETURNING:
      // lo propagamos para que el caller use el id directo sin re-buscar por matrícula.
      if (response.data && typeof response.data === 'number') {
        return response.data
      }
    } catch (error) {
      console.error('Error inserting embarcacion:', error)
    }
    return false
  }

  async updateEmbarcacion(
    embarcacionData: Omit<IEmbarcacion, 'fecha_creacion' | 'fecha_actualizacion' | 'usuario_creacion'>,
  ): Promise<boolean> {
    try {
      const body = {
        op_update_embarcacion: {
          id: embarcacionData.id,
          nombre_embarcacion: embarcacionData.nombre_embarcacion,
          numero_matricula: embarcacionData.numero_matricula,
          puerto: embarcacionData.puerto,
          eslora_metros: embarcacionData.eslora_metros,
          manga_metros: embarcacionData.manga_metros,
          tipo_embarcacion: embarcacionData.tipo_embarcacion,
          capacidad_pasajeros: embarcacionData.capacidad_pasajeros,
          capacidad_pasajeros_adicional: embarcacionData.capacidad_pasajeros_adicional,
          nave_madre: embarcacionData.nave_madre,
          id_lancha: embarcacionData.id_lancha,
          tripulacion_exigida: embarcacionData.tripulacion_exigida,
          activo: embarcacionData.activo,
          id_matricula: embarcacionData.id_matricula,
          id_fotografía: embarcacionData.id_fotografía,
          id_certifricado_navegacion: embarcacionData.id_certifricado_navegacion,
          autorizacion: 0,
          anio_fabricacion: this.formatYearToISO(embarcacionData.anio_fabricacion),
          fecha_vencimiento:
            !embarcacionData.fecha_vencimiento ||
            typeof embarcacionData.fecha_vencimiento === 'object' ||
            embarcacionData.fecha_vencimiento.includes('@nil')
              ? '1970-01-01T00:00:00.000Z'
              : embarcacionData.fecha_vencimiento,
          convenio: embarcacionData.convenio,
        },
      }

      const response = await dataQueryManager.addQuery<number>({
        query: `${import.meta.env.VITE_BASE_URL}/enrolamiento/1/op_update_embarcacion`,
        operation: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        params: [],
        body,
      })
      if (response.data && typeof response.data === 'number') {
        return true
      }
      throw new Error(`Error en actualización de embarcación (status ${response.status})`)
    } catch (error) {
      console.error('Error updating embarcacion:', error)
      throw error
    }
  }
}
